import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import '../styles/LoginScreen.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function callEdgeFunction(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(45);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputsRef = useRef([]);

  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const digit = value.slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError('');
    if (digit && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const next = [...otp];
        next[index] = '';
        setOtp(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setLoading(true);
    try {
      const data = await callEdgeFunction('verify-otp', {
        email,
        otp_code: code,
        purpose: 'email_verification',
      });
      if (!data?.success) {
        const msg = data?.error || 'Invalid or expired code.';
        if (typeof data?.attemptsRemaining === 'number') {
          setError(`${msg} (${data.attemptsRemaining} attempt${data.attemptsRemaining !== 1 ? 's' : ''} remaining)`);
        } else {
          setError(msg);
        }
        return;
      }
      // OTP confirmed — mark this user as email-verified so login won't re-gate.
      // Uses the active session (signup or login both have one here).
      await supabase.rpc('mark_email_verified');
      navigate('/standouts/pending-approval');
    } catch (err) {
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCountdown(45);
    setOtp(['', '', '', '', '', '']);
    setError('');
    inputsRef.current[0]?.focus();
    try {
      await callEdgeFunction('send-otp', {
        email,
        purpose: 'email_verification',
      });
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="login-screen">
      <div className="login-container" style={{ maxWidth: 440 }}>

        <div className="login-header" style={{ marginBottom: 32 }}>
          <div className="login-logo-wrapper">
            <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
          </div>
          <h1 className="login-title">Verify your email</h1>
          <p className="login-subtitle">We sent a 6-digit code to</p>
          <p className="verify-email-highlight">{email || 'your email'}</p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="otp-boxes">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => (inputsRef.current[i] = el)}
                className={`otp-box${digit ? ' otp-box-filled' : ''}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                autoFocus={i === 0}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {error && (
            <div className="login-error" style={{ marginTop: 16 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <p className="verify-resend-row">
            Didn't receive the code?{' '}
            <button
              type="button"
              className={`verify-resend-btn${canResend ? '' : ' verify-resend-disabled'}`}
              onClick={handleResend}
              disabled={!canResend}
            >
              Resend
            </button>
            {!canResend && (
              <span className="verify-countdown"> (0:{pad(countdown)})</span>
            )}
          </p>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
            style={{ marginTop: 24 }}
          >
            {loading ? 'Verifying...' : 'Verify Code →'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => navigate('/standouts/login')}
          >
            ← Back
          </button>
        </div>

      </div>

      <div className="login-background-glow" />
      <div className="login-background-pattern" />
    </div>
  );
};

export default VerifyEmail;
